import { supabase } from '../lib/supabase'
import type { User, UserUpdate, SocialLinks } from '../types/database.types'

// Public profile view (limited fields)
export interface PublicProfile {
  id: string
  full_name: string | null
  avatar_url: string | null
  bio: string | null
  specialty: string | null
  location: string | null
  company_name: string | null
  website_url: string | null
  social_links: SocialLinks | null
}

export const profileService = {
  /**
   * Get a public provider profile
   */
  async getPublicProfile(userId: string): Promise<PublicProfile | null> {
    const { data, error } = await supabase
      .from('users')
      .select(
        'id, full_name, avatar_url, bio, specialty, location, company_name, website_url, social_links'
      )
      .eq('id', userId)
      .eq('is_public_profile', true)
      .single()

    if (error) {
      if (error.code === 'PGRST116') return null // Not found
      throw error
    }

    return data as unknown as PublicProfile
  },

  /**
   * Get multiple public profiles
   */
  async getPublicProfiles(userIds: string[]): Promise<PublicProfile[]> {
    if (userIds.length === 0) return []

    const { data, error } = await supabase
      .from('users')
      .select(
        'id, full_name, avatar_url, bio, specialty, location, company_name, website_url, social_links'
      )
      .in('id', userIds)
      .eq('is_public_profile', true)

    if (error) throw error
    return data as unknown as PublicProfile[]
  },

  /**
   * Get featured providers (for homepage)
   */
  async getFeaturedProviders(limit = 6): Promise<PublicProfile[]> {
    const { data, error } = await supabase
      .from('users')
      .select(
        'id, full_name, avatar_url, bio, specialty, location, company_name, website_url, social_links'
      )
      .eq('is_public_profile', true)
      .eq('role', 'provider')
      .limit(limit)

    if (error) throw error
    return data as unknown as PublicProfile[]
  },

  /**
   * Search providers by specialty or name
   */
  async searchProviders(
    query: string,
    limit = 20
  ): Promise<PublicProfile[]> {
    const { data, error } = await supabase
      .from('users')
      .select(
        'id, full_name, avatar_url, bio, specialty, location, company_name, website_url, social_links'
      )
      .eq('is_public_profile', true)
      .eq('role', 'provider')
      .or(`full_name.ilike.%${query}%,specialty.ilike.%${query}%`)
      .limit(limit)

    if (error) throw error
    return data as unknown as PublicProfile[]
  },

  /**
   * Get the current user's full profile
   */
  async getCurrentProfile(): Promise<User | null> {
    const {
      data: { user },
    } = await supabase.auth.getUser()

    if (!user) return null

    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('id', user.id)
      .single()

    if (error) {
      if (error.code === 'PGRST116') return null
      throw error
    }

    return data
  },

  /**
   * Update the current user's profile
   */
  async updateCurrentProfile(updates: UserUpdate): Promise<User> {
    const {
      data: { user },
    } = await supabase.auth.getUser()

    if (!user) {
      throw new Error('Must be authenticated to update profile')
    }

    const { data, error } = await supabase
      .from('users')
      .update(updates)
      .eq('id', user.id)
      .select()
      .single()

    if (error) throw error
    return data
  },

  /**
   * Update a user's profile by ID
   */
  async updateProfile(userId: string, updates: UserUpdate): Promise<User | null> {
    const { data, error } = await supabase
      .from('users')
      .update(updates)
      .eq('id', userId)
      .select()
      .single()

    if (error) {
      if (error.code === 'PGRST116') return null
      throw error
    }
    return data
  },

  /**
   * Upload avatar image
   * Note: This requires Supabase Storage to be set up
   */
  async uploadAvatar(userId: string, file: File): Promise<string> {
    const fileExt = file.name.split('.').pop()
    const fileName = `${userId}-${Date.now()}.${fileExt}`
    const filePath = `avatars/${fileName}`

    const { error: uploadError } = await supabase.storage
      .from('avatars')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: true,
      })

    if (uploadError) throw uploadError

    const {
      data: { publicUrl },
    } = supabase.storage.from('avatars').getPublicUrl(filePath)

    // Update user profile with new avatar URL
    await profileService.updateProfile(userId, { avatar_url: publicUrl })

    return publicUrl
  },

  /**
   * Toggle public profile visibility
   */
  async togglePublicProfile(isPublic: boolean): Promise<User> {
    return profileService.updateCurrentProfile({ is_public_profile: isPublic })
  },

  /**
   * Get profile stats for dashboard
   */
  async getProfileStats(userId: string): Promise<{
    totalVideos: number
    totalViews: number
    totalLikes: number
  }> {
    // Get video counts
    const { data: videos, error: videosError } = await supabase
      .from('videos')
      .select('id, view_count, like_count')
      .eq('user_id', userId)

    if (videosError) throw videosError

    const totalVideos = videos.length
    const totalViews = videos.reduce((sum, v) => sum + (v.view_count ?? 0), 0)
    const totalLikes = videos.reduce((sum, v) => sum + (v.like_count ?? 0), 0)

    return {
      totalVideos,
      totalViews,
      totalLikes,
    }
  },
}
